"use client";

/**
 * Cookie consent flow, reinstated 2026-07-10 (design-audit Phase 0).
 *
 * History: the banner shipped for TES-519, was disabled in production, and
 * the dead UI was removed on 2026-05-05 (20adee9a) leaving `useCookieConsent`
 * as a no-op that force-granted all consent. That meant GA4 + AdSense loaded
 * for every visitor (including EU locales) with no choice, while the privacy
 * policy still described a working banner.
 *
 * This restores the panel AND makes the hook honest: analytics/marketing are
 * OFF until the visitor decides. Consumers (`ga4-provider`, `ad-placement`,
 * `adsense-loader`) already gate on these flags, so third-party scripts now
 * load only after consent.
 */

import { useState, useEffect, useRef, startTransition, useId } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { MOTION_DURATION, MOTION_EASE } from "@/lib/motion-theme";
import { X, Check } from "lucide-react";
import { getClientConsentRegime, type ConsentRegime } from "@/lib/consent-regime";
import { readStorage, writeStorage } from "@/lib/safe-storage";
import { Button } from "@/components/ui/button";
import Link from "next/link";

const CONSENT_KEY = "teslalocksound_cookie_consent";
const CONSENT_VERSION = "1.0";
export const COOKIE_CONSENT_REOPEN_EVENT = "cookieConsentReopen";
const CONSENT_UPDATE_EVENT = "cookieConsentUpdate";
/**
 * Set on <body> for exactly as long as the banner is rendered, alongside
 * `--tls-consent-h` (its measured height). app/globals.css lets any docked
 * surface marked `data-yields-to-consent` lift above the banner instead of
 * being covered by it — the mini player and the sticky install CTA.
 */
export const CONSENT_PENDING_CLASS = "tls-consent-pending";
const CONSENT_HEIGHT_VAR = "--tls-consent-h";

interface ConsentPreferences {
  version: string;
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

function getStoredConsent(): ConsentPreferences | null {
  try {
    const stored = readStorage(CONSENT_KEY);
    if (!stored) return null;
    const consent = JSON.parse(stored);
    if (consent.version !== CONSENT_VERSION) return null;
    return consent;
  } catch {
    return null;
  }
}

function saveConsent(preferences: Omit<ConsentPreferences, "version" | "timestamp">): void {
  if (typeof window === "undefined") return;
  const consent: ConsentPreferences = {
    ...preferences,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  // A failed write falls through on purpose so the in-memory event still fires
  // and the current page session honors the choice.
  writeStorage(CONSENT_KEY, JSON.stringify(consent));
  window.dispatchEvent(new CustomEvent(CONSENT_UPDATE_EVENT, { detail: consent }));
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always on; cannot be disabled
    analytics: false,
    marketing: false,
  });
  const titleId = useId();
  const descriptionId = useId();
  const prefersReducedMotion = useReducedMotion();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // §07.5 "Focus moves into the banner on appearance and is not trapped".
  // The heading, not a button: a screen reader then reads the title and the
  // description (aria-describedby) before the choices, and no choice is
  // pre-selected by focus. Runs on the auto-show and on every footer reopen.
  useEffect(() => {
    if (!isVisible) return;
    headingRef.current?.focus({ preventScroll: true });
  }, [isVisible]);

  // Publish the banner's presence and height to <body> so docked surfaces
  // below it can yield (see CONSENT_PENDING_CLASS). ResizeObserver rather
  // than a fixed number: the panel is 180px collapsed, ~430px with settings
  // open, and taller still at 200% text.
  useEffect(() => {
    if (!isVisible) return;
    const body = document.body;
    const wrapper = wrapperRef.current;
    body.classList.add(CONSENT_PENDING_CLASS);
    const publish = () => {
      if (!wrapper) return;
      // The wrapper's own box, NOT its distance to the viewport bottom. It is
      // `bottom-0` and carries the panel's outer inset (px-3 pb-3 / sm:pb-4 /
      // md:pb-6), so its height is exactly the band the banner occupies — and
      // unlike a viewport-relative read it is immune to the entry animation.
      //
      // Measuring `innerHeight - panel.top` published 231px for a band that
      // actually measured 252px (TES-2320): the first read lands mid-spring,
      // while `translateY` still holds the panel ~21px low, and ResizeObserver
      // never corrected it because a transform does not change the observed
      // box size. Everything downstream under-reserved by that much — the
      // docked surfaces that yield, and the scroll reserve in globals.css.
      const height = Math.ceil(wrapper.getBoundingClientRect().height);
      body.style.setProperty(CONSENT_HEIGHT_VAR, `${Math.max(height, 0)}px`);
    };
    publish();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    if (wrapper) observer?.observe(wrapper);
    return () => {
      observer?.disconnect();
      body.classList.remove(CONSENT_PENDING_CLASS);
      body.style.removeProperty(CONSENT_HEIGHT_VAR);
    };
  }, [isVisible, showDetails]);

  useEffect(() => {
    // Auto-show only in opt-in jurisdictions (geo-gated 2026-07-11) and only
    // when no decision is stored. Elsewhere the banner stays reachable via the
    // footer "Cookie preferences" link (the reopen event below).
    // startTransition keeps the banner out of the LCP-critical path (ecf30d96).
    if (getClientConsentRegime() === "required" && !getStoredConsent()) {
      startTransition(() => setIsVisible(true));
    }

    const handleReopen = () => {
      setIsVisible(true);
      setShowDetails(true);
    };

    window.addEventListener(COOKIE_CONSENT_REOPEN_EVENT, handleReopen);
    return () => window.removeEventListener(COOKIE_CONSENT_REOPEN_EVENT, handleReopen);
  }, []);

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    saveConsent(preferences);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={
            prefersReducedMotion
              ? { opacity: 0 }
              : { opacity: 0, transform: "translateY(24px)" }
          }
          animate={
            prefersReducedMotion
              ? { opacity: 1 }
              : { opacity: 1, transform: "translateY(0px)" }
          }
          exit={
            prefersReducedMotion
              ? { opacity: 0 }
              : { opacity: 0, transform: "translateY(16px)" }
          }
          transition={
            prefersReducedMotion
              ? { duration: MOTION_DURATION.fast, ease: MOTION_EASE.emphasis }
              : { type: "spring", damping: 28, stiffness: 240, mass: 0.9 }
          }
          // --tls-z-consent (49): the consent tier sits BELOW every modal (z-50 — report,
          // guide, share, keyboard shortcuts, the mobile menu sheet and its
          // scrim), because §07.5 makes this a notice, "not a decision that
          // gates the product": a modal opened while consent is pending must
          // win. At the old z-[200] the banner covered the report modal's
          // Submit. It stays above the docked mobile surfaces — the sticky
          // install CTA (z-40) and the mini player (49, mounted earlier in
          // <body>, so this later sibling paints over it at equal z) — which
          // yield through body.tls-consent-pending (app/globals.css) rather
          // than cover it. The iOS download overlay (z-200) and the command
          // palette (9998) remain above.
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[var(--tls-z-consent)] px-3 pb-3 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
          ref={wrapperRef}
        >
        <div className="mx-auto flex w-full max-w-6xl justify-center sm:justify-end">
          {/* §07.5 "max 760": one width in both states, so opening the
              settings does not re-flow the banner's edge. */}
          <div className="pointer-events-auto w-full sm:max-w-[47.5rem]">
            <div
              role="dialog"
              aria-modal="false"
              aria-labelledby={titleId}
              aria-describedby={descriptionId}
              data-testid="cookie-consent-panel"
              // §07.5 "Esc is equivalent to Reject". Scoped to the dialog
              // (focus is moved into it on show) rather than the document, so
              // Esc inside a modal opened over a pending banner closes THAT
              // modal and does not also record a rejection.
              onKeyDown={event => {
                if (event.key !== "Escape" || event.defaultPrevented) return;
                event.preventDefault();
                handleRejectAll();
              }}
              // §07.5 "1px ink top rule": a top hairline only, not a four-sided
              // frame — this is a docked notice, not a card. The light theme's
              // --ls-elev-dock and the dark theme's 1px edge are the lift.
              className="overflow-hidden rounded-[var(--ls-radius-0)] border-t border-[color:var(--tls-border-strong)] bg-[var(--tls-surface)] [box-shadow:var(--ls-elev-dock)]"
            >
              {/* §07.5 "20 padding". */}
              <div className="p-5">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3
                          id={titleId}
                          ref={headingRef}
                          // Programmatic focus target only (tabIndex -1): it
                          // is not a tab stop, and the DS ring is withheld
                          // because the focus was not the reader's keystroke.
                          tabIndex={-1}
                          className="font-display text-base font-bold text-[var(--tls-fg-strong)] outline-none sm:text-lg"
                        >
                          Cookie preferences
                        </h3>
                        <p
                          id={descriptionId}
                          className="mt-1 max-w-[32rem] text-[0.6875rem] leading-[1.35rem] text-[var(--tls-fg-muted)] sm:text-sm sm:leading-5"
                        >
                          Necessary cookies keep previews and downloads working. Choose whether
                          analytics and ads can run while you browse.{" "}
                          <Link
                            href="/privacy-policy"
                            className="text-[var(--tls-brand-ink)] underline underline-offset-2 transition-colors hover:text-[var(--tls-brand-ink-hover)]"
                          >
                            Privacy details
                          </Link>
                          .
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleRejectAll}
                        data-testid="cookie-consent-close"
                        className="-mr-1 -mt-1 inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-[var(--radius-pill)] text-[var(--tls-fg-subtle)] transition-colors hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-muted)]"
                        aria-label="Reject non-essential cookies and close"
                      >
                        <X className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      {/* §02.1 through the primitive, no skin overrides. Every
                          choice is the ink-outlined secondary: §07.5 "Reject is
                          as prominent as Accept: same size, same position,
                          unstyled difference only", so this panel has NO
                          accent-filled primary. Filling Accept would make one
                          answer to a consent question the preferred one. */}
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => setShowDetails(!showDetails)}
                        data-testid="cookie-consent-settings"
                        className="font-semibold"
                      >
                        {showDetails ? "Hide settings" : "Cookie settings"}
                      </Button>

                      <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={handleRejectAll}
                          data-testid="cookie-consent-reject-all"
                          className="font-semibold"
                        >
                          Reject all
                        </Button>

                        {showDetails ? (
                          <Button
                            variant="secondary"
                            type="button"
                            onClick={handleAcceptSelected}
                            data-testid="cookie-consent-save-preferences"
                            className="font-semibold"
                          >
                            <Check className="h-4 w-4" aria-hidden="true" />
                            Save preferences
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            type="button"
                            onClick={handleAcceptAll}
                            data-testid="cookie-consent-accept-all"
                            className="font-semibold"
                          >
                            Accept all
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {showDetails && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={
                        prefersReducedMotion
                          ? { duration: MOTION_DURATION.fast, ease: MOTION_EASE.emphasis }
                          : { duration: MOTION_DURATION.standard, ease: MOTION_EASE.emphasis }
                      }
                      className="overflow-hidden"
                    >
                      <div className="mt-3 border-t border-[color:var(--tls-border-subtle)] pt-3">
                        <div>
                          <div className="flex flex-col gap-3 border-b border-[color:var(--tls-border-subtle)] py-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <h4 className="text-sm font-medium text-[var(--tls-fg-strong)]">
                                Necessary cookies
                              </h4>
                              <p className="mt-0.5 text-xs text-[var(--tls-fg-muted)]">
                                Required for the website to function properly.
                              </p>
                            </div>
                            <div className="inline-flex items-center gap-2 self-start rounded-[var(--radius-pill)] border border-[color:color-mix(in_srgb,var(--tls-brand-ink)_26%,transparent)] px-3 py-2 text-xs font-medium text-[var(--tls-brand-ink)] sm:self-center">
                              <span className="h-2 w-2 rounded-[var(--radius-pill)] bg-[var(--tls-brand-ink)]" />
                              Always active
                            </div>
                          </div>

                          <ConsentToggleRow
                            id="cookie-analytics-label"
                            title="Analytics cookies"
                            description="Help us understand how visitors use the site."
                            checked={preferences.analytics}
                            onToggle={() =>
                              setPreferences(p => ({ ...p, analytics: !p.analytics }))
                            }
                          />

                          <ConsentToggleRow
                            id="cookie-marketing-label"
                            title="Marketing cookies"
                            description="Used to show relevant advertisements."
                            checked={preferences.marketing}
                            onToggle={() =>
                              setPreferences(p => ({ ...p, marketing: !p.marketing }))
                            }
                            last
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ConsentToggleRow({
  id,
  title,
  description,
  checked,
  onToggle,
  last = false,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
  /** Omits the row's bottom hairline — this is the last row in the group. */
  last?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between ${
        last ? "" : "border-b border-[color:var(--tls-border-subtle)]"
      }`}
    >
      <div>
        <h4 id={id} className="text-sm font-medium text-[var(--tls-fg-strong)]">
          {title}
        </h4>
        <p className="mt-0.5 text-xs text-[var(--tls-fg-muted)]">{description}</p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[var(--radius-pill)] self-start sm:self-center"
        role="switch"
        aria-checked={checked}
        aria-labelledby={id}
      >
        <span
          className={`flex h-6 w-10 items-center rounded-[var(--radius-pill)] px-1 transition-colors ${
            checked
              ? "justify-end bg-[color:color-mix(in_srgb,var(--tls-brand)_30%,var(--tls-surface-hover))]"
              : "justify-start bg-[var(--tls-surface-hover)]"
          }`}
        >
          <span
            className={`h-4 w-4 rounded-[var(--radius-pill)] transition-colors ${
              checked ? "bg-[var(--tls-brand)]" : "bg-[var(--tls-fg-subtle)]"
            }`}
          />
        </span>
      </button>
    </div>
  );
}

/**
 * Consent status for script gates. Analytics/marketing are OFF until the
 * visitor makes a choice; `hasConsent` is false while the banner is pending.
 */
export function useCookieConsent() {
  // MUST start null on the client too: the server renders with no consent, so
  // the first client render has to match or consented visitors get a React
  // hydration mismatch (#418) wherever ad/analytics gates change the tree.
  // localStorage + the regime cookie are read in the effect below, after
  // hydration.
  const [consent, setConsent] = useState<ConsentPreferences | null>(null);
  const [regime, setRegime] = useState<ConsentRegime | null>(null);

  useEffect(() => {
    // Hydrate from storage after mount (also covers updates from the banner
    // in this or another tab via the consent event).
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Hydrating state from localStorage on mount is valid
    setConsent(getStoredConsent());
    // eslint-disable-next-line react-hooks/set-state-in-effect -- same post-hydration read, cookie-backed
    setRegime(getClientConsentRegime());

    const handleUpdate = (event: CustomEvent<ConsentPreferences>) => {
      setConsent(event.detail);
    };

    window.addEventListener(CONSENT_UPDATE_EVENT, handleUpdate as EventListener);
    return () => window.removeEventListener(CONSENT_UPDATE_EVENT, handleUpdate as EventListener);
  }, []);

  // Geo-gated defaults (2026-07-11): a stored decision always wins; without
  // one, opt-in jurisdictions stay OFF until the banner is answered while
  // everywhere else defaults ON (opt-out available via the footer link).
  const impliedAllowed = consent === null && regime === "optional";

  return {
    hasConsent: consent !== null || regime === "optional",
    analyticsAllowed: consent ? consent.analytics : impliedAllowed,
    marketingAllowed: consent ? consent.marketing : impliedAllowed,
    consent,
  };
}
